import {NavigateFunction} from 'react-router-dom';
import {getUserTokenOrRedirect} from '../../shared/services/auth.service';
import {AdminBillingDto, AdminCompanyBillingDto, AdminSubscriptionDto, AdminUsageDto} from './dtos/admin-billing.dto';

/** A refused billing request: the server's message (Stripe off here, customer already made, Stripe's own refusal). */
export class AdminBillingError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AdminBillingError';
    this.status = status;
  }
}

async function billingRequest<T>(navigate: NavigateFunction, path: string, method: 'GET' | 'POST'): Promise<T> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/billing${path}`,
    {method, headers: {Authorization: `Bearer ${token}`}});
  if (!response.ok) {
    const body = await response.json().catch(() => null) as {message?: string} | null;
    throw new AdminBillingError(response.status, body?.message || `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

export const getBilling = (navigate: NavigateFunction) =>
  billingRequest<AdminBillingDto>(navigate, '', 'GET');

export const createStripeCustomer = (navigate: NavigateFunction, companyId: number) =>
  billingRequest<AdminCompanyBillingDto>(navigate, `/companies/${companyId}/stripe-customer`, 'POST');

export const syncBilling = (navigate: NavigateFunction, companyId: number) =>
  billingRequest<AdminCompanyBillingDto>(navigate, `/companies/${companyId}/sync`, 'POST');

// --- Formatting shared by the tab and its tests ---------------------------

const money = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});
const whole = new Intl.NumberFormat('en-US', {maximumFractionDigits: 0});

export const formatCents = (cents: number) => money.format(cents / 100);

export const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'});

/** "40 a month", "40 a month, yearly", "Plan not read" (a price without the metadata), or "No plan". */
export const planLabel = (subscription: AdminSubscriptionDto | null) => {
  if (!subscription) return 'No plan';
  if (subscription.planPerMonth == null) return 'Plan not read';
  const perMonth = whole.format(subscription.planPerMonth);
  return subscription.cycle === 'annual' ? `${perMonth} a month, yearly` : `${perMonth} a month`;
};

/** "12 of 40" with the allowance, "12" without one. */
export const usageLabel = (usage: AdminUsageDto) =>
  usage.allowance == null ? whole.format(usage.used) : `${whole.format(usage.used)} of ${whole.format(usage.allowance)}`;

/** "Active", "Past due", "Canceled", "Cancels at period end"; "No subscription" without one. */
export const statusLabel = (subscription: AdminSubscriptionDto | null) => {
  if (!subscription) return 'No subscription';
  if (subscription.cancelAtPeriodEnd && subscription.status === 'active') return 'Cancels at period end';
  const words = subscription.status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export type StatusTone = 'success' | 'info' | 'warning' | 'error' | 'default';

export const statusTone = (subscription: AdminSubscriptionDto | null): StatusTone => {
  if (!subscription) return 'default';
  if (subscription.cancelAtPeriodEnd) return 'warning';
  switch (subscription.status) {
    case 'active': return 'success';
    case 'trialing': return 'info';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return 'error';
    default: return 'default';
  }
};

